import React from 'react';
import Api from '../../../utils/Api';
import BadgeGrid from '../../../composant/Dashboard/BadgeGrid';
import Item from '@mui/material/Grid';
import BadgeCreateForm from '../../../composant/Forms/Badge/BadgeCreateForm';
import { Button, Dialog, Snackbar, Alert } from '@mui/material';
import './../Dashboard.css';
import { Add } from '@mui/icons-material';
import Loading from '../../../composant/Loading/LoadingComponent';
import { RoleIds } from '../../../policies/Role';

// Page modifié par Bethermat Frédérick 2026-04-16

/**
 * Onglet de gestion des badges du tableau de bord.
 *
 * Affiche la liste des badges accessibles selon le rôle de l'utilisateur connecté :
 *  - Administrateur (Admin / AdminContact) : voit tous les badges créés par les professeurs.
 *  - Professeur : voit uniquement ses propres badges.
 */
class BadgesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      closeBadgeForm: false, // Contrôle l'ouverture du formulaire de création de badge
      showSuccessMessage: false, // Contrôle l'affichage de la Snackbar de succès
      successMessage: '', // Message de succès à afficher dans la Snackbar
      showErrorMessage: false, // Contrôle l'affichage de la Snackbar d'erreur
      errorMessage: '', // Message d'erreur à afficher dans la Snackbar
      badges: [], // Liste des badges récupérés depuis l'API
      charge: false, // Indique si le chargement initial des données est terminé
      role: 0, // Identifiant du rôle de l'utilisateur connecté
    };

    this.handleBadgeForm = this.handleBadgeForm.bind(this);
    this.getBadges = this.getBadges.bind(this);
    this.addBadge = this.addBadge.bind(this);
    this.editBadge = this.editBadge.bind(this);
    this.deleteBadge = this.deleteBadge.bind(this);
    this.errorBadge = this.errorBadge.bind(this);
    this.handleCloseSuccessMessage = this.handleCloseSuccessMessage.bind(this);
    this.handleCloseErrorMessage = this.handleCloseErrorMessage.bind(this);
  }

  /**
   * Au montage du composant, on récupère le rôle de l'utilisateur connecté
   */
  componentDidMount() {
    this.fetchUser();
  }

  /**
   * Récupère les informations de l'utilisateur connecté depuis l'API.
   * Logique reprise de UserTab.js.
   */
  fetchUser() {
    Api.get('/auth/current_user')
      .then((response) => {
        // Le rôle est stocké avant d'appeler getBadges pour que l'URL soit correcte
        this.setState({ role: response.data.role_id }, this.getBadges);
      })
      .catch(() => {
        this.errorBadge('Erreur lors de la récupération du rôle.');
      });
  }

  /**
   * Récupère la liste des badges depuis l'API selon le rôle de l'utilisateur.
   *  - Admin : récupère tous les badges créés par les professeurs.
   * Marque le chargement comme terminé une fois les données reçues.
   */
  getBadges() {
    let url = '/badge/my-badges-prof';

    if (
      this.state.role === RoleIds.Admin ||
      this.state.role === RoleIds.AdminContact
    ) {
      url = '/badge/all-prof-badges';
    }

    Api.get(url)
      .then((res) => {
        const badges = res.data;
        this.setState({ badges: badges, charge: true });
      })
      .catch(() => {
        this.errorBadge('Erreur lors de la récupération des badges.');
      });
  }

  /**
   * Bascule l'affichage du formulaire de création de badge (ouverture / fermeture).
   */
  handleBadgeForm() {
    this.setState({ closeBadgeForm: !this.state.closeBadgeForm });
  }

  /**
   * Callback appelé après la création réussie d'un badge.
   * Recharge la liste complète des badges et affiche une notification de succès.
   */
  addBadge() {
    this.getBadges();
    this.setState({
      successMessage: 'Le badge a été ajouté avec succès !',
      showSuccessMessage: true,
    });
  }

  /**
   * Met à jour localement le badge modifié dans la liste sans rappel API
   */
  editBadge(badge) {
    const badges = this.state.badges.map((b) => {
      if (b.id === badge.id) {
        return badge; // Remplace l'ancien badge par la version mise à jour
      }
      return b;
    });
    this.setState({
      badges,
      successMessage: 'Le badge a été modifié avec succès !',
      showSuccessMessage: true,
    });
  }

  /**
   * Retire le badge supprimé de la liste locale sans rappel API,
   */
  deleteBadge(badge) {
    const badges = this.state.badges.filter((b) => b.id !== badge.id);
    this.setState({
      badges,
      successMessage: 'Le badge a été supprimé avec succès !',
      showSuccessMessage: true,
    });
  }

  /**
   * Ferme la Snackbar de succès et réinitialise son message.
   */
  handleCloseSuccessMessage() {
    this.setState({ showSuccessMessage: false, successMessage: '' });
  }

  /**
   * Affiche la Snackbar d'erreur avec le message fourni.
   */
  errorBadge(message) {
    this.setState({ errorMessage: message, showErrorMessage: true });
  }

  /**
   * Ferme la Snackbar d'erreur et réinitialise son message.
   */
  handleCloseErrorMessage() {
    this.setState({ showErrorMessage: false, errorMessage: '' });
  }

  render() {
    return (
      <Item className="bordered">
        <div className="title">
          <h4>Liste des badges</h4>

          {/* Bouton d'ouverture du formulaire de création de badge */}
          <Button
            variant="contained"
            onClick={this.handleBadgeForm}
            startIcon={<Add></Add>}
          >
            Créer un badge
          </Button>

          {/* Formulaire de création affiché en plein écran dans un Dialog */}
          <Dialog
            fullScreen
            open={this.state.closeBadgeForm}
            onClose={this.handleBadgeForm}
            transitionDuration={0}
          >
            <BadgeCreateForm
              handleClose={this.handleBadgeForm}
              addBadge={this.addBadge}
              errorBadge={this.errorBadge}
            />
          </Dialog>
        </div>

        {/* Grille affichant les badges avec les callbacks de modification, suppression et erreur */}
        <BadgeGrid
          rows={this.state.badges}
          refresh={this.getBadges}
          deleteBadge={this.deleteBadge}
          editBadge={this.editBadge}
          errorBadge={this.errorBadge}
        />

        {/* Snackbar de succès : affichée après un ajout, une modification ou une suppression */}
        <Snackbar
          onClose={this.handleCloseSuccessMessage}
          open={this.state.showSuccessMessage}
          autoHideDuration={3000}
        >
          <Alert
            onClose={this.handleCloseSuccessMessage}
            severity="success"
            sx={{ width: '100%' }}
            md={{ minWidth: '300px' }}
          >
            {this.state.successMessage}
          </Alert>
        </Snackbar>

        {/* Snackbar d'erreur : affichée en cas d'échec d'une opération API */}
        <Snackbar
          onClose={this.handleCloseErrorMessage}
          open={this.state.showErrorMessage}
          autoHideDuration={3000}
        >
          <Alert
            onClose={this.handleCloseErrorMessage}
            severity="error"
            sx={{ width: '100%' }}
            md={{ minWidth: '300px' }}
          >
            {this.state.errorMessage}
          </Alert>
        </Snackbar>

        {/* Séparateur affiché une fois les données chargées, sinon indicateur de chargement */}
        {this.state.charge ? <hr></hr> : <Loading />}
      </Item>
    );
  }
}

export default BadgesTab;
